import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import routes from "./routes";
import ConditionalRoute from "@/components/ConditionalRoute/ConditionalRoute";
import PreLogin from "@/layouts/PreLogin/PreLogin";
import PostLogin from "@/layouts/PostLogin/PostLogin";
import PageNotFound from "@/pages/PageNotFound/PageNotFound";
import { AUTH_ROUTE_PATHS } from "@/pages/Auth/authConfig";

export interface RouteConfig {
  path: string;
  element?: React.ReactNode; // make optional
  children?: Record<string, RouteConfig> | RouteConfig[];
  pageName?: string;
  sidebar?: { show: boolean };
  permissionName?: string;
  
}

const AuthOutlet = () => <Outlet />;

// Mobile payment links land on `/?userId=…&eventId=…` — forward those (query
// intact) to checkout; a bare `/` still goes home.
const CHECKOUT_PARAMS = ["userId", "eventId"];

const RootRedirect = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const isCheckoutLink = CHECKOUT_PARAMS.some((key) => params.has(key));

  return (
    <Navigate
      replace
      to={
        isCheckoutLink
          ? `${routes.privateRoutes.CHECKOUT.path}${search}`
          : routes.privateRoutes.HOME.path
      }
    />
  );
};

const {
  WELCOME,
  QUESTIONS,
  INTRODUCTION,
  CHECKOUT,
  ...appPrivateRoutes
} = routes.privateRoutes;

const privateOnboardingRoutes = { WELCOME, QUESTIONS, INTRODUCTION };

const buildRoutes = (
  routesInput: Record<string, RouteConfig> | RouteConfig[]
): RouteObject[] => {
  const routesArray: RouteConfig[] = Array.isArray(routesInput)
    ? routesInput
    : Object.values(routesInput);

  return routesArray.map(({ path, element, children }) => ({
    path,
    ...(element ? { element } : {}), // only include element if it exists
    ...(children ? { children: buildRoutes(children) } : {}),
  }));
};


const router = createBrowserRouter([
  // Open routes
  ...buildRoutes(routes.routes),

  // Checkout — the one URL mobile redirects to for both membership and ticket
  // payments (`isTicket` picks the flow, see pages/Checkout). Requires login
  // but renders standalone (its own header/footer), so it sits outside the
  // PostLogin layout. Logged-out visitors are sent to login and brought back
  // here with the original query string (see ConditionalRoute + SignIn).
  {
    element: <ConditionalRoute isPrivate component={AuthOutlet} />,
    children: buildRoutes([CHECKOUT]),
  },

  // Pre-login layout: public auth + authenticated onboarding (welcome, questions, introduction)
  {
    element: <ConditionalRoute component={PreLogin} />,
    children: [
      ...buildRoutes(routes.publicRoutes),
      {
        element: <ConditionalRoute isPrivate component={AuthOutlet} />,
        children: buildRoutes(privateOnboardingRoutes),
      },
    ],
  },

  // Post-login layout: authenticated app pages
  {
    element: <ConditionalRoute isPrivate component={PostLogin} />,
    children: [
      ...buildRoutes(appPrivateRoutes),
      {
        path: "/",
        element: <RootRedirect />,
      },
    ],
  },

  // Catch-all
  {
    path: "*",
    element: <PageNotFound />,
  },
]);

export default router;
